import React, { useState, useEffect } from 'react';
import { Settings, Globe, Lock, ChevronDown, ChevronUp, Edit, ExternalLink, Loader2, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DevGeneralSettings = () => {
  const [expandedSections, setExpandedSections] = useState(['site']);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [settings, setSettings] = useState({
    site_name: 'AMINO-CHAIN Peptides',
    site_url: 'https://amino-chain.com',
    logo_url: '',
    favicon_url: '',
    admin_email: '',
    support_email: '',
    maintenance_mode: false,
    debug_mode: false,
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin-settings/site`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Error fetching site settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/admin-settings/site`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Settings Saved', description: 'Site settings updated successfully.' });
      setIsEditing(false);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save settings.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev =>
      prev.includes(section)
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const handleToggle = async (field) => {
    const newSettings = { ...settings, [field]: !settings[field] };
    setSettings(newSettings);
    
    // Auto-save toggle changes
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/admin-settings/site`, newSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Setting Updated', description: `${field.replace('_', ' ')} ${newSettings[field] ? 'enabled' : 'disabled'}.` });
    } catch (error) {
      setSettings(settings); // Revert on error
      toast({ title: 'Error', description: 'Failed to update setting.', variant: 'destructive' });
    }
  };

  const SettingsSection = ({ id, title, description, children, locked = false }) => {
    const isExpanded = expandedSections.includes(id);
    
    return (
      <Card className="mb-4">
        <CardHeader 
          className="cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => toggleSection(id)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Settings className="w-5 h-5 text-[#6e2ea8]" />
              </div>
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  {title}
                  {locked && <Lock className="w-4 h-4 text-gray-400" />}
                </CardTitle>
                <CardDescription>{description}</CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {locked && <Badge variant="outline">Locked</Badge>}
              {!isEditing && !locked && id === 'site' && (
                <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setIsEditing(true); }}>
                  <Edit className="w-4 h-4 mr-1" />
                  Edit
                </Button>
              )}
              {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
            </div>
          </div>
        </CardHeader>
        {isExpanded && (
          <CardContent className="pt-0">
            {children}
          </CardContent>
        )}
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#6e2ea8]" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl" data-testid="dev-general-settings">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Globe className="w-7 h-7 text-[#6e2ea8]" />
            General Settings
          </h2>
          <p className="text-gray-500 mt-1">Configure site-wide settings for logo, favicon, and system modes</p>
        </div>
        <a href="/" target="_blank" rel="noopener noreferrer">
          <Button variant="outline" size="sm">
            <ExternalLink className="w-4 h-4 mr-2" />
            View Site
          </Button>
        </a>
      </div>

      {/* Site Settings */}
      <SettingsSection 
        id="site" 
        title="Site Settings" 
        description="Logo, favicon, site name, and URL configuration"
      >
        <div className="space-y-6 pt-4">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <Label>Site Name</Label>
              <Input 
                value={settings.site_name}
                onChange={(e) => setSettings({...settings, site_name: e.target.value})}
                disabled={!isEditing}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Displayed in browser tab and site header</p>
            </div>
            <div>
              <Label>Site URL</Label>
              <Input 
                value={settings.site_url}
                onChange={(e) => setSettings({...settings, site_url: e.target.value})}
                disabled={!isEditing}
                className="mt-1"
                placeholder="https://amino-chain.com"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <Label>Logo URL</Label>
              <Input 
                value={settings.logo_url}
                onChange={(e) => setSettings({...settings, logo_url: e.target.value})}
                disabled={!isEditing}
                className="mt-1"
                placeholder="https://example.com/logo.png"
              />
              <p className="text-xs text-gray-500 mt-1">Used across all pages and location pages</p>
            </div>
            <div className="flex items-center justify-center bg-gray-100 rounded-lg p-4 min-h-[80px]">
              {settings.logo_url ? (
                <img src={settings.logo_url} alt="Logo preview" className="max-h-16 max-w-full" />
              ) : (
                <span className="text-gray-400 text-sm">No logo set</span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <Label>Favicon URL (32x32)</Label>
              <Input 
                value={settings.favicon_url}
                onChange={(e) => setSettings({...settings, favicon_url: e.target.value})}
                disabled={!isEditing}
                placeholder="https://example.com/favicon.ico"
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Browser tab icon</p>
            </div>
            <div className="flex items-center justify-center bg-gray-100 rounded-lg p-4">
              {settings.favicon_url ? (
                <img src={settings.favicon_url} alt="Favicon preview" className="w-8 h-8" />
              ) : (
                <span className="text-gray-400 text-sm">No favicon</span>
              )}
            </div>
          </div>

          {isEditing && (
            <div className="flex gap-2 pt-4 border-t">
              <Button onClick={saveSettings} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Changes
              </Button>
              <Button variant="outline" onClick={() => { setIsEditing(false); fetchSettings(); }}>
                Cancel
              </Button>
            </div>
          )}
        </div>
      </SettingsSection>

      {/* Contact Settings */}
      <SettingsSection 
        id="contact" 
        title="Contact Settings" 
        description="Admin and support email addresses"
      >
        <div className="space-y-4 pt-4">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <Label>Admin Email</Label>
              <Input 
                type="email"
                value={settings.admin_email}
                onChange={(e) => setSettings({...settings, admin_email: e.target.value})}
                disabled={!isEditing}
                className="mt-1"
                placeholder="admin@amino-chain.com"
              />
            </div>
            <div>
              <Label>Support Email</Label>
              <Input 
                type="email"
                value={settings.support_email}
                onChange={(e) => setSettings({...settings, support_email: e.target.value})}
                disabled={!isEditing}
                className="mt-1"
                placeholder="support@amino-chain.com"
              />
            </div>
          </div>
          <p className="text-xs text-gray-400">
            Note: Contact email for public display is managed in Business Settings
          </p>
        </div>
      </SettingsSection>

      {/* System Settings */}
      <SettingsSection 
        id="system" 
        title="System Settings" 
        description="Maintenance mode and debug options"
      >
        <div className="space-y-4 pt-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium">Maintenance Mode</p>
              <p className="text-sm text-gray-500">Show maintenance page to visitors (admins can still access)</p>
            </div>
            <Switch 
              checked={settings.maintenance_mode}
              onCheckedChange={() => handleToggle('maintenance_mode')}
            />
          </div>
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium">Debug Mode</p>
              <p className="text-sm text-gray-500">Enable detailed error logging and diagnostics</p>
            </div>
            <Switch 
              checked={settings.debug_mode}
              onCheckedChange={() => handleToggle('debug_mode')}
            />
          </div>
          
          {settings.maintenance_mode && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>Warning:</strong> Maintenance mode is enabled. Visitors will see a maintenance page.
              </p>
            </div>
          )}
        </div>
      </SettingsSection>

      {/* Info Box */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <p className="text-sm text-blue-800">
            <strong>Settings Sync:</strong> Logo and site name settings are used across:
          </p>
          <ul className="text-sm text-blue-700 mt-2 list-disc list-inside space-y-1">
            <li>Main site header and navigation</li>
            <li>All 382 generated location pages</li>
            <li>Email templates and invoices</li>
            <li>Browser tab (favicon and title)</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default DevGeneralSettings;
